import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Check, ChevronDown, CircleDashed, Clock, GitPullRequestArrow, Pencil, Play, RotateCcw, Users } from "lucide-react";

import {
  approveStage,
  cancelWorkflow,
  getWorkflow,
  requestStageChanges,
  resumeWorkflow,
  type WorkflowEvent,
  type WorkflowStage,
} from "@/api/submission_workflow";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { WorkflowBuilderDialog, type WorkflowBuilderMode } from "@/components/plan_review/WorkflowBuilderDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

type SubmissionApprovalsCardProps = {
  municipalityId: number;
  submissionId: number;
};

function formatEventSummary(event: WorkflowEvent): string {
  const actor = event.actor_email ?? "Someone";
  const stageName = (event.metadata.stage_name as string) ?? "";
  switch (event.event_type) {
    case "workflow_started":
      return `${actor} started the workflow`;
    case "stage_approved":
      return `${actor} approved ${stageName}`;
    case "stage_changes_requested":
      return `${actor} requested changes at ${stageName}`;
    case "workflow_resumed":
      return `${actor} resumed the workflow at ${stageName}`;
    case "workflow_completed":
      return "Workflow completed; submission marked completed";
    case "workflow_canceled":
      return `${actor} canceled the workflow`;
    case "stages_updated":
      return `${actor} updated the stage list`;
    case "stage_assignee_changed":
      return `${actor} changed the assignee on ${stageName}`;
    case "report_emailed":
      return `${actor} emailed the feedback report to ${(event.metadata.recipient as string) ?? "the applicant"}`;
    default:
      return event.event_type;
  }
}

function StageStatusIcon({ stage }: { stage: WorkflowStage }) {
  switch (stage.status) {
    case "approved":
      return <Check className="h-4 w-4 text-emerald-600" aria-label="Approved" />;
    case "in_progress":
      return <Clock className="h-4 w-4 text-violet-600" aria-label="In progress" />;
    case "changes_requested":
      return <GitPullRequestArrow className="h-4 w-4 text-amber-600" aria-label="Changes requested" />;
    default:
      return <CircleDashed className="h-4 w-4 text-muted-foreground/60" aria-label="Pending" />;
  }
}

export function SubmissionApprovalsCard({ municipalityId, submissionId }: SubmissionApprovalsCardProps) {
  const queryClient = useQueryClient();
  const workflowKey = ["submission_workflow", submissionId] as const;
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderMode, setBuilderMode] = useState<WorkflowBuilderMode>("start");
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [decidingStageId, setDecidingStageId] = useState<number | null>(null);
  const [decisionNote, setDecisionNote] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: workflow, isError } = useQuery({
    queryKey: workflowKey,
    queryFn: () => getWorkflow(municipalityId, submissionId),
    retry: false,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: workflowKey });
    // Queue rows show the current stage; refresh them too.
    queryClient.invalidateQueries({ queryKey: ["submissions"] });
  };

  const showMutationError = (mutationError: Error) => {
    setErrorMessage(mutationError.message);
  };

  const decideMutation = useMutation({
    mutationFn: ({ stageId, action }: { stageId: number; action: "approve" | "request_changes" }) =>
      action === "approve"
        ? approveStage(municipalityId, submissionId, stageId, decisionNote.trim() || undefined)
        : requestStageChanges(municipalityId, submissionId, stageId, decisionNote.trim() || undefined),
    onSuccess: () => {
      invalidate();
      setDecidingStageId(null);
      setDecisionNote("");
      setErrorMessage(null);
    },
    onError: showMutationError,
  });

  const resumeMutation = useMutation({
    mutationFn: () => resumeWorkflow(municipalityId, submissionId),
    onSuccess: () => {
      invalidate();
      setErrorMessage(null);
    },
    onError: showMutationError,
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelWorkflow(municipalityId, submissionId),
    onSuccess: () => {
      invalidate();
      setCancelConfirmOpen(false);
      setErrorMessage(null);
    },
  });

  // Users without intake access get a 403 here; the card simply hides.
  if (isError || !workflow) return null;

  const paused = workflow.stages.some((stage) => stage.status === "changes_requested");
  const totalStages = workflow.stages.length;
  const canManageWorkflow = workflow.can_manage;

  const openBuilder = (mode: WorkflowBuilderMode) => {
    setBuilderMode(mode);
    setBuilderOpen(true);
  };

  return (
    <Card
      data-testid="submission-approvals-card"
      className="flex min-h-0 shrink-0 flex-col overflow-hidden lg:max-h-[min(38vh,22rem)]"
    >
      <CardHeader className="shrink-0 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">Approvals</CardTitle>
          {workflow.started ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              aria-expanded={expanded}
              aria-controls="submission-approvals-card-body"
              aria-label={expanded ? "Collapse approvals" : "Expand approvals"}
              onClick={() => setExpanded((open) => !open)}
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "" : "-rotate-90"}`} />
            </Button>
          ) : null}
          <div className="flex-1" />
          {!workflow.started ? (
            <Button type="button" size="sm" onClick={() => openBuilder("start")}>
              <Play className="mr-1.5 h-3.5 w-3.5" />
              Start workflow
            </Button>
          ) : null}
          {canManageWorkflow ? (
            <>
              <Button type="button" size="sm" variant="outline" onClick={() => openBuilder("edit")}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Edit stages
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => setCancelConfirmOpen(true)}
              >
                <Ban className="mr-1.5 h-3.5 w-3.5" />
                Cancel workflow
              </Button>
            </>
          ) : null}
          {paused ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={resumeMutation.isPending}
              onClick={() => resumeMutation.mutate()}
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Resume
            </Button>
          ) : null}
        </div>
        {errorMessage ? (
          <p className="text-sm text-destructive" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </CardHeader>
      <CardContent
        id="submission-approvals-card-body"
        data-testid="submission-approvals-card-body"
        hidden={!expanded}
        className="min-h-0 flex-1 space-y-3 lg:ordinal-scrollbar lg:overflow-y-auto lg:overscroll-y-contain"
      >
        {!workflow.started ? (
          <p className="text-sm text-muted-foreground">
            Route this plan through your review stages. Each team signs off before the next stage
            begins.
          </p>
        ) : (
          <ol className="space-y-2">
            {workflow.stages.map((stage) => (
              <li
                key={stage.id}
                className={
                  stage.status === "in_progress"
                    ? "rounded-lg border-2 border-violet-400 p-2.5"
                    : "rounded-lg border p-2.5"
                }
                data-testid="approval-stage-row"
              >
                <div className="flex items-center gap-2 text-sm">
                  <StageStatusIcon stage={stage} />
                  <span className="font-medium">
                    {stage.position} · {stage.name}
                  </span>
                  {stage.assignee_email ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      <Users className="h-3 w-3" />
                      {stage.assignee_email}
                    </span>
                  ) : null}
                  <div className="flex-1" />
                  {stage.decided_at ? (
                    <span className="text-xs text-muted-foreground">
                      {stage.decided_by_email} · {formatRelativeTime(stage.decided_at)}
                    </span>
                  ) : null}
                </div>
                {stage.note ? (
                  <p className="mt-1.5 pl-6 text-xs text-muted-foreground">{stage.note}</p>
                ) : null}
                {stage.status === "in_progress" ? (
                  decidingStageId === stage.id ? (
                    <div className="mt-2 space-y-2 pl-6">
                      <Textarea
                        value={decisionNote}
                        onChange={(event) => setDecisionNote(event.target.value)}
                        rows={2}
                        placeholder="Optional note for the record…"
                        aria-label="Decision note"
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={decideMutation.isPending}
                          onClick={() => decideMutation.mutate({ stageId: stage.id, action: "approve" })}
                        >
                          <Check className="mr-1.5 h-3.5 w-3.5" />
                          Approve stage
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={decideMutation.isPending}
                          onClick={() =>
                            decideMutation.mutate({ stageId: stage.id, action: "request_changes" })
                          }
                        >
                          Request changes
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setDecidingStageId(null);
                            setDecisionNote("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 pl-6">
                      <Button type="button" size="sm" onClick={() => setDecidingStageId(stage.id)}>
                        Decide stage {stage.position} of {totalStages}
                      </Button>
                    </div>
                  )
                ) : null}
              </li>
            ))}
          </ol>
        )}

        {workflow.events.length > 0 ? (
          <div className="space-y-1 border-t pt-2" data-testid="workflow-event-timeline">
            {workflow.events.slice(0, 6).map((event) => (
              <p key={event.id} className="text-xs text-muted-foreground">
                {formatEventSummary(event)} · {formatRelativeTime(event.created_at)}
              </p>
            ))}
          </div>
        ) : null}
      </CardContent>

      <WorkflowBuilderDialog
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        municipalityId={municipalityId}
        submissionId={submissionId}
        mode={builderMode}
        existingStages={builderMode === "edit" ? workflow.stages : []}
        onComplete={invalidate}
      />

      <AlertDialog
        open={cancelConfirmOpen}
        onOpenChange={(open) => {
          setCancelConfirmOpen(open);
          if (!open) cancelMutation.reset();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this workflow?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the stage list so you can start over. Recorded stage decisions are
              cleared. The activity timeline keeps a note that the workflow was canceled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {cancelMutation.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {cancelMutation.error.message}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Keep workflow</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={cancelMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                cancelMutation.mutate();
              }}
            >
              {cancelMutation.isPending ? "Canceling…" : "Cancel workflow"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
